import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { useAudio } from '../contexts/AudioContext';

const AudioUploader = ({ songId, onAudioAdded, existingAudioFiles = [] }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [audioFiles, setAudioFiles] = useState(existingAudioFiles);
  const [versionName, setVersionName] = useState('Original');
  const [versionDescription, setVersionDescription] = useState('');
  const [defaultVersionId, setDefaultVersionId] = useState(
    existingAudioFiles.length > 0 && existingAudioFiles.find(file => file.isDefault) 
      ? existingAudioFiles.find(file => file.isDefault).id 
      : existingAudioFiles.length > 0 ? existingAudioFiles[0].id : null
  );
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const { playTrack, currentTrack, isPlaying } = useAudio();

  // Supported audio formats
  const supportedFormats = [
    'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/wave', 'audio/x-wav',
    'audio/ogg', 'audio/webm', 'audio/aac', 'audio/flac', 'audio/mp4', 'audio/x-m4a',
    'audio/x-m4a', 'audio/m4a'
  ];

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format duration for display (convert seconds to mm:ss)
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get audio duration from file
  const getAudioDuration = (file) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.preload = 'metadata';
      
      audio.onloadedmetadata = () => {
        window.URL.revokeObjectURL(audio.src);
        resolve(audio.duration);
      };
      
      audio.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if file type is supported
    if (!supportedFormats.includes(file.type)) {
      setError(`Unsupported file format: ${file.type}. Please upload an audio file (MP3, WAV, OGG, etc).`);
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Get audio duration
      const duration = await getAudioDuration(file);
      const formattedDuration = formatDuration(duration);

      // Get pre-signed URL for upload
      const uploadUrlResponse = await axios.get(
        `${config.API_URL}/upload-url?fileType=${encodeURIComponent(file.type)}&songId=${songId}&versionName=${encodeURIComponent(versionName)}`
      );

      const { uploadUrl, fileUrl, fileKey } = uploadUrlResponse.data;
      console.log('Received fileKey from upload URL:', fileKey);

      // Upload file to S3
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      // Get a pre-signed URL for viewing the uploaded audio file
      const presignedResponse = await axios.get(`${config.API_URL}/get-presigned-url?key=${encodeURIComponent(fileKey)}`);
      const presignedUrl = presignedResponse.data.presignedUrl;
      console.log('Received presigned URL for playback:', presignedUrl);
      
      // Create new audio file object
      const newAudioFile = {
        id: Date.now().toString(),
        name: versionName,
        description: versionDescription,
        url: presignedUrl, // Use pre-signed URL for immediate playback
        fileKey, // Store the fileKey for persistence - CRITICAL for persistence after refresh
        fileType: file.type,
        fileSize: formatFileSize(file.size),
        duration: formattedDuration,
        durationSeconds: duration,
        uploadedAt: new Date().toISOString(),
        isDefault: audioFiles.length === 0 // Make it default if it's the first file
      };
      
      // Verify fileKey is present and valid
      if (!fileKey || fileKey.trim() === '') {
        console.error('Error: fileKey is missing or empty!', fileKey);
        setError('Failed to get file storage key. Audio may not persist after refresh.');
      } else {
        console.log('Created new audio file with fileKey:', fileKey);
      }

      // Update audio files state
      const updatedAudioFiles = [...audioFiles, newAudioFile];
      
      // Validate all audio files have fileKeys
      const missingFileKeys = updatedAudioFiles.filter(file => !file.fileKey).map(file => file.id);
      if (missingFileKeys.length > 0) {
        console.error('WARNING: Some audio files are missing fileKeys and may not persist:', missingFileKeys);
      } else {
        console.log('All audio files have fileKeys - good for persistence!');
      }
      
      setAudioFiles(updatedAudioFiles);

      // Call the callback function to update parent component
      if (onAudioAdded) {
        console.log('Sending audio files to parent with fileKeys:', 
          updatedAudioFiles.map(f => ({ id: f.id, name: f.name, fileKey: f.fileKey })));
        onAudioAdded(updatedAudioFiles, formattedDuration);
      }

      // Reset form
      setVersionName('Original');
      setVersionDescription('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading audio file:', error);
      setError('Failed to upload audio file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveAudio = (index) => {
    const updatedAudioFiles = [...audioFiles];
    const removedFile = updatedAudioFiles[index];
    updatedAudioFiles.splice(index, 1);
    
    // If the removed file was the default, set a new default if there are files left
    if (removedFile.isDefault && updatedAudioFiles.length > 0) {
      updatedAudioFiles[0].isDefault = true;
      setDefaultVersionId(updatedAudioFiles[0].id);
    } else if (updatedAudioFiles.length === 0) {
      setDefaultVersionId(null);
    }
    
    // Validate all remaining audio files have fileKeys
    const missingFileKeys = updatedAudioFiles.filter(file => !file.fileKey).map(file => file.id);
    if (missingFileKeys.length > 0) {
      console.error('WARNING: Some remaining audio files are missing fileKeys and may not persist:', missingFileKeys);
    } else if (updatedAudioFiles.length > 0) {
      console.log('All remaining audio files have fileKeys - good for persistence!');
    }
    
    setAudioFiles(updatedAudioFiles);
    
    // Call the callback function to update parent component
    if (onAudioAdded) {
      console.log('Sending updated audio files to parent after removal:', 
        updatedAudioFiles.map(f => ({ id: f.id, name: f.name, fileKey: f.fileKey })));
      onAudioAdded(updatedAudioFiles);
    }
  };
  
  const setAsDefault = (id) => {
    const updatedAudioFiles = audioFiles.map(file => ({
      ...file,
      isDefault: file.id === id
    }));
    
    setAudioFiles(updatedAudioFiles);
    setDefaultVersionId(id);
    
    // Call the callback function to update parent component
    if (onAudioAdded) {
      // Make sure we're passing the complete audio files with fileKeys for persistence
      console.log('Setting default audio file, checking fileKeys:', 
        updatedAudioFiles.map(f => ({ id: f.id, hasFileKey: !!f.fileKey, fileKey: f.fileKey })));
      
      // Verify all audio files have fileKeys for persistence
      const allHaveFileKeys = updatedAudioFiles.every(file => !!file.fileKey);
      if (!allHaveFileKeys) {
        console.warn('Some audio files are missing fileKeys - they may not persist after refresh');
      }
      
      onAudioAdded(updatedAudioFiles);
    }
  };

  const playAudio = async (audioFile) => {
    try {
      console.log('Playing audio file:', {
        id: audioFile.id,
        name: audioFile.name,
        hasFileKey: !!audioFile.fileKey,
        fileKey: audioFile.fileKey
      });
      
      // Prepare the track object for the audio context
      const track = {
        id: audioFile.id,
        title: audioFile.name,
        fileKey: audioFile.fileKey,
        duration: audioFile.durationSeconds,
        // Include additional metadata
        version: audioFile.name,
        description: audioFile.description,
        songId: songId
      };
      
      // Play the track using the global audio context
      playTrack(track);
    } catch (error) {
      console.error('Error setting up audio playback:', error);
      setError(`Failed to play audio: ${error.message}`);
    }
  };
  
  // We no longer need audio event listeners as we're using the global audio context

  return (
    <div className="mt-4">
      <h3 className="text-lg font-medium text-gray-300 mb-2">Audio Files</h3>
      
      {/* We're using the global audio player now */}
      
      {/* List of uploaded audio files */}
      {audioFiles.length > 0 && (
        <div className="mb-4 bg-gray-800 rounded-md overflow-hidden">
          <div className="p-3 bg-gray-700 border-b border-gray-600">
            <h4 className="text-white font-medium">Audio Versions</h4>
          </div>
          {audioFiles.map((audio, index) => (
            <div key={audio.id} className={`p-4 border-b border-gray-700 last:border-0 ${audio.isDefault ? 'bg-gray-700' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <button 
                    onClick={() => playAudio(audio)}
                    className={`w-8 h-8 ${currentTrack && currentTrack.id === audio.id && isPlaying ? 'bg-red-600' : 'bg-indigo-600'} rounded-full flex items-center justify-center mr-3 text-white hover:bg-indigo-500 transition-colors`}
                  >
                    {currentTrack && currentTrack.id === audio.id && isPlaying ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </button>
                  <div>
                    <div className="flex items-center">
                      <span className="text-white font-medium">{audio.name}</span>
                      {audio.isDefault && (
                        <span className="ml-2 px-2 py-0.5 bg-indigo-900 text-indigo-200 text-xs rounded-full">Default</span>
                      )}
                    </div>
                    <div className="text-gray-400 text-xs">{audio.duration} • {audio.fileSize}</div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {!audio.isDefault && (
                    <button
                      onClick={() => setAsDefault(audio.id)}
                      className="text-gray-400 hover:text-indigo-400 transition-colors"
                      title="Set as default version"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveAudio(index)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete version"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              {audio.description && (
                <div className="mt-1 text-gray-400 text-sm pl-11">
                  {audio.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Upload form */}
      <div className="bg-gray-800 rounded-md p-4">
        <div className="mb-3">
          <label htmlFor="versionName" className="block text-sm font-medium text-gray-300 mb-1">
            Version Name
          </label>
          <input
            type="text"
            id="versionName"
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., Original, Acoustic, Demo"
            disabled={isUploading}
          />
        </div>
        
        <div className="mb-3">
          <label htmlFor="versionDescription" className="block text-sm font-medium text-gray-300 mb-1">
            Version Description (optional)
          </label>
          <textarea
            id="versionDescription"
            value={versionDescription}
            onChange={(e) => setVersionDescription(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Brief description of this version"
            disabled={isUploading}
            rows={2}
          />
        </div>
        
        <div className="mb-3">
          <label htmlFor="audioFile" className="block text-sm font-medium text-gray-300 mb-1">
            Audio File
          </label>
          <input
            type="file"
            id="audioFile"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="audio/*"
            className="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={isUploading}
          />
          <p className="mt-1 text-xs text-gray-400">
            Supported formats: MP3, WAV, OGG, AAC, FLAC, WebM
          </p>
        </div>
        
        {isUploading && (
          <div className="mb-3">
            <div className="w-full bg-gray-700 rounded-full h-2.5">
              <div 
                className="bg-indigo-600 h-2.5 rounded-full" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-400 text-center">
              Uploading: {uploadProgress}%
            </p>
          </div>
        )}
        
        {error && (
          <div className="mb-3 text-red-500 text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioUploader;
